/**
 * Smart, locale-aware message timestamp helpers (WhatsApp/Telegram style).
 *
 * - `formatMessageTime(ts)` returns a short label suitable for the time-stamp
 *   shown on each individual message bubble.
 * - `formatDaySeparator(ts)` returns the longer label shown on the sticky
 *   day-separator pill between messages from different days.
 * - `sameDay(a, b)` compares two ISO timestamps by calendar day in the
 *   viewer's local timezone (used to decide when to insert a separator).
 *
 * All output uses `Intl.DateTimeFormat` with the user's default locale so
 * the order/wording adapts automatically (e.g. "28 May 2025, 10:26" in
 * European locales vs "May 28, 2025, 10:26 AM" in en-US).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function diffDays(a, b) {
  return Math.round((startOfDay(a) - startOfDay(b)) / DAY_MS);
}

const fmtTime = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

const fmtWeekday = new Intl.DateTimeFormat(undefined, { weekday: 'short' });

const fmtMonthDay = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
});

const fmtFullDate = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

/** Returns true if both timestamps fall on the same calendar day (viewer's TZ). */
export function sameDay(a, b) {
  if (!a || !b) return false;
  return startOfDay(new Date(a)).getTime() === startOfDay(new Date(b)).getTime();
}

/**
 * Per-bubble timestamp:
 *   - Today                  → "10:26 AM"
 *   - Yesterday              → "Yesterday 10:26 AM"
 *   - Within last 7 days     → "Mon 10:26 AM"
 *   - Same calendar year     → "May 28, 10:26 AM"
 *   - Older                  → "May 28, 2025, 10:26 AM"
 */
export function formatMessageTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const days = diffDays(now, d);
  const time = fmtTime.format(d);

  if (days <= 0) return time;
  if (days === 1) return `Yesterday ${time}`;
  if (days < 7) return `${fmtWeekday.format(d)} ${time}`;
  if (d.getFullYear() === now.getFullYear()) return `${fmtMonthDay.format(d)}, ${time}`;
  return `${fmtFullDate.format(d)}, ${time}`;
}

/**
 * Day-separator pill label:
 *   - Today / Yesterday / This-week weekday name / "May 28" / "May 28, 2025"
 */
export function formatDaySeparator(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const days = diffDays(now, d);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return fmtWeekday.format(d);
  if (d.getFullYear() === now.getFullYear()) return fmtMonthDay.format(d);
  return fmtFullDate.format(d);
}
